import axios from "axios";
import cookieHelper from "helpers/getCookieData";
import { encryptData, decryptData } from "./crypto";
import {
  encryptAndPackRequest,
  unpackAndDecryptResponse,
  createEncryptedSessionHeader
} from "./e2e_crypto";

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:4017/api'}`;

const axiosApi = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Accept': 'application/octet-stream, application/json',
  },
});

// Avoid infinite loop during token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Request Interceptor: Attach Auth Token & Enforce 100% Binary E2E Encrypted Transmission
 */
axiosApi.interceptors.request.use(
  async (config) => {
    const token = cookieHelper.getCookie("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Skip encryption for FormData (uploads), Blobs, public key endpoints, or explicit skip headers
    const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
    const isBlob = typeof Blob !== 'undefined' && config.data instanceof Blob;
    const isPubKey = config.url?.includes('public-key');
    const shouldSkip = isFormData || isBlob || isPubKey || config.headers['x-skip-encryption'] === 'true' || config.headers['x-no-encrypt'] === 'true';

    if (!shouldSkip) {
      try {
        if (config.data) {
          // 1. Data Mutating Requests (POST / PUT / PATCH): Pack payload into binary frame
          const { binaryBuffer, cryptoSessionKey } = await encryptAndPackRequest(config.data, API_URL);
          config.data = binaryBuffer;
          config.headers['Content-Type'] = 'application/octet-stream';
          config.headers['x-e2e-binary'] = 'true';
          config.responseType = 'arraybuffer';
          config._e2eSessionKey = cryptoSessionKey;
        } else {
          // 2. Query Requests (GET / DELETE): Send RSA-encrypted session key header to get binary response
          const { base64EncryptedKey, cryptoSessionKey } = await createEncryptedSessionHeader(API_URL);
          config.headers['x-e2e-session-key'] = base64EncryptedKey;
          config.headers['x-e2e-binary'] = 'true';
          config.responseType = 'arraybuffer';
          config._e2eSessionKey = cryptoSessionKey;
        }
      } catch (e2eErr) {
        console.warn('⚠️ E2E Binary encryption handshake fallback:', e2eErr.message);
        if (config.data) {
          config.data = { data: encryptData(config.data) };
          config.headers['Content-Type'] = 'application/json';
          config.headers['x-encrypted'] = 'true';
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response Interceptor: Decrypt Binary E2E Frame / Encrypted JSON & Handle 401 Refresh
 */
axiosApi.interceptors.response.use(
  async (response) => {
    // 1. Binary E2E Response Decryption
    if (response.data instanceof ArrayBuffer && response.config?._e2eSessionKey) {
      try {
        const decrypted = await unpackAndDecryptResponse(response.data, response.config._e2eSessionKey);
        if (decrypted !== null) {
          response.data = decrypted;
          return response;
        }
      } catch (decErr) {
        console.error('Binary response decryption failed:', decErr.message);
      }
    }

    // If ArrayBuffer was returned without session key (e.g. plain JSON as ArrayBuffer)
    if (response.data instanceof ArrayBuffer) {
      try {
        const text = new TextDecoder('utf-8').decode(response.data);
        response.data = JSON.parse(text);
      } catch {
        // Keep as ArrayBuffer if non-JSON
      }
    }

    // 2. Legacy Encrypted JSON Envelope Decryption
    if (response.data && typeof response.data === 'object' && typeof response.data.data === 'string') {
      const decrypted = decryptData(response.data.data);
      if (decrypted !== null) {
        response.data = decrypted;
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Try decrypting error body if ArrayBuffer or string
    if (error.response?.data instanceof ArrayBuffer && originalRequest?._e2eSessionKey) {
      try {
        const decryptedErr = await unpackAndDecryptResponse(error.response.data, originalRequest._e2eSessionKey);
        if (decryptedErr !== null) {
          error.response.data = decryptedErr;
        }
      } catch {
        try {
          const text = new TextDecoder('utf-8').decode(error.response.data);
          error.response.data = JSON.parse(text);
        } catch {
          // ignore
        }
      }
    } else if (error.response?.data && typeof error.response.data === 'object' && typeof error.response.data.data === 'string') {
      const decryptedError = decryptData(error.response.data.data);
      if (decryptedError !== null) {
        error.response.data = decryptedError;
      }
    }

    // If 401 Unauthorized and not retried yet
    if (error.response?.status === 401 && !originalRequest?._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axiosApi(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = cookieHelper.getCookie('refresh_token');

      if (!refreshToken) {
        return Promise.reject(error);
      }

      try {
        const { body } = await axios.post(`${API_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const newAccessToken = body?.access_token;
        if (newAccessToken) {
          cookieHelper.setCookie('access_token', newAccessToken, 1);
          axiosApi.defaults.headers.common['Authorization'] = 'Bearer ' + newAccessToken;
          processQueue(null, newAccessToken);

          originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
          return axiosApi(originalRequest);
        }
      } catch (err) {
        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export async function get(url, config = {}) {
  return await axiosApi.get(url, { ...config }).then((response) => response.data);
}

export async function post(url, data, config = {}) {
  const finalData = {
    ...data,
    client_id: localStorage.getItem('client') || undefined,
    clinic_id: localStorage.getItem('clinic') || undefined,
  };

  return axiosApi
    .post(url, finalData, { ...config })
    .then((response) => response.data);
}

export async function put(url, data, config = {}) {
  const finalData = {
    ...data,
    client_id: localStorage.getItem('client') || undefined,
    clinic_id: localStorage.getItem('clinic') || undefined,
  };

  return axiosApi
    .put(url, finalData, { ...config })
    .then((response) => response.data);
}

export async function del(url, config = {}) {
  return await axiosApi
    .delete(url, { ...config })
    .then((response) => response.data);
}

export async function uploadImg(url, formData, config = {}) {
  return axiosApi
    .post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'x-skip-encryption': 'true',
      },
      ...config,
    })
    .then((response) => response.data);
}

export default axiosApi;
